import React, { useState } from 'react';
import {
  Modal,
  FormField,
  Textarea,
  Button,
  SpaceBetween,
  Icon,
  Box,
  Alert,
} from '@cloudscape-design/components';

interface CancelTaskModalProps {
  visible: boolean;
  onDismiss: () => void;
  onConfirm: (data: { reason: string }) => void;
}

export const CancelTaskModal: React.FC<CancelTaskModalProps> = ({
  visible,
  onDismiss,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    if (!reason.trim()) {
      setError('Reason for cancellation is required');
      return false;
    }
    setError('');
    return true;
  };

  const handleConfirm = async () => {
    if (validateForm()) {
      setIsLoading(true);
      try {
        await onConfirm({ reason: reason.trim() });
        setReason('');
        setError('');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDismiss = () => {
    setReason('');
    setError('');
    onDismiss();
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header={
        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
          <Icon name="status-negative" variant="error" />
          <span>Cancel Task</span>
        </SpaceBetween>
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="normal"
              onClick={handleDismiss}
              disabled={isLoading}
              data-testid="go-back-button"
            >
              Go Back
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={isLoading}
              disabled={isLoading}
              data-testid="cancel-task-button"
            >
              Cancel Task
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <Alert type="warning" statusIconAriaLabel="Warning">
          Are you sure you want to cancel this task? This action cannot be undone.
        </Alert>

        <FormField
          label={
            <span>
              Reason for cancellation <i style={{ color: '#d91515' }}>*</i>
            </span>
          }
          errorText={error}
        >
          <Textarea
            value={reason}
            onChange={({ detail }) => setReason(detail.value)}
            placeholder="Please provide a reason for cancellation"
            rows={4}
            data-testid="reason-for-cancellation-field"
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};
